import useCreateProject from "@custom-hooks/useCreateProject";
import usePublishProject from "@custom-hooks/usePublishProject";
import useSaveSubscriber from "@custom-hooks/useSaveSubscriber";
import isBlank from "@sedan-utils/is-blank";
import { getIt } from "@uikit/getIt";
import { AISaasLogoIcon } from "@uiview/views/AISaas/AISaasPreview/icons";
import { useCreation } from "ahooks";
import { Spin } from "antd";
import { motion } from "framer-motion";
import { Dataload } from "imagica-corekit/dist/base/cutil/Dataload";
import { tryPromise } from "imagica-corekit/dist/base/cutil/LangUtil";
import { ReadyStatus } from "imagica-corekit/dist/base/cutil/ReadyStatus";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { CotFlow } from "imagica-corekit/dist/cases/cot/CotFlow";
import { PreviewService } from "imagica-corekit/dist/cases/preview/PreviewService";
import { WebConfigStore } from "imagica-corekit/dist/cases/webconfig/WebConfigStore";
import { useDataload } from "imagica-uikit/dist/hooks/useDataload";
import { get } from "lodash";
import { useState } from "react";
import { ChatPanel } from "./ChatPanel";
import styles from "./Cot.module.scss";
import { CotBloc } from "./CotBloc";
import { CotUtil } from "./CotUtil";
import { ListData, ListPanel } from "./ListPanel";
import { ShareToAppModal } from "./ShareToAppModal";
import * as constants from "./constants";
import { logEvent } from "@uikit/service/amplitude";

const variants = {
  normal: { opacity: 1 },
  creating: { opacity: 0 },
};

type CotProps = {
  clickLogOut: () => void;
};

export function Cot(props: CotProps) {
  const cotFlow = getIt(CotFlow);
  const previewService = getIt(PreviewService);

  const { publishProject } = usePublishProject();
  const { createProject } = useCreateProject();

  const bloc = useCreation(() => new CotBloc(), []);

  const result = useDataload(() => {
    return new Dataload(() => getIt(WebConfigStore).get());
  }, []).value;

  const selectedUI = "saas";

  const { setSubscriberParamsByQ } = useSaveSubscriber();
  const [animate, setAnimate] = useState<"normal" | "creating">("normal");

  const handleClick = async (item: ListData) => {
    bloc.state.creating.value = true;
    setAnimate("creating");

    bloc.state.sharableLink.value = undefined;

    const result = await cotFlow.getTemplate(item.label);

    const template = CotUtil.generateData(result, item.label);

    const updated_project = await createProject(template);
    const sharedProject = await publishProject(updated_project, updated_project, null);

    const link = await previewService.getLink({
      index: sharedProject?.index,
      structureLinkParams: {
        uuid: sharedProject.uuid,
        appId: "appId-1",
        enablePreviewApp: true,
        selectedUI,
        type: "ai_app",
      },
    });

    const match = link.match(/[\?&]q=([^&]*)/);

    const qValue = match && match[1];

    const subscribeResult = await tryPromise(setSubscriberParamsByQ(qValue));
    logEvent("create_project_for_mobile", {
      project_id: subscribeResult.data.attributes.project_id,
      q: qValue,
    });
    const newUrl = `${window.location.origin}${window.location.pathname}#/editor`;

    bloc.state.sharableLink.value = `${newUrl}?q=${qValue}`;
    bloc.state.creating.value = false;
    bloc.state.question.value = constants.APP_GENERATION_IS_COMPLETE;
    setAnimate("normal");
  };

  const handleClose = (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {};

  const handleClickLogout = () => {
    props.clickLogOut();
  };

  const sharableLink = bloc.state.sharableLink.value;
  const placeholder = isBlank(sharableLink)
    ? constants.TRY_THESE_EXAMPLE_STARTER_PROMPTS
    : constants.TRY_ANOTHER_PROMPT;

  return (
    <div className={styles.cotWrapper}>
      {/* TODO: Need product confirmation to add or not Log out  */}
      {/* <div className={styles.logout} onClick={handleClickLogout}>
        Log out
      </div> */}
      <div className={styles.logo}>
        <AISaasLogoIcon />
      </div>
      <ChatPanel bloc={bloc} onClose={handleClose} startMotionComplete={true} />
      <motion.div initial={variants.normal} variants={variants} animate={animate} transition={{ duration: 0.5 }}>
        <LinkToButton link={sharableLink} />
        <div className={styles.placeholder}>{placeholder}</div>
        {result.status === ReadyStatus.LOADING && <Spin />}
        {result.status === ReadyStatus.DONE && (
          <ListPanel onClick={handleClick} options={get(result, "data.cot.examples", [])} />
        )}
        {sharableLink && <ShareToAppModal />}
      </motion.div>
    </div>
  );
}

export function LinkToButton(props: { link?: string }) {
  const handleClick = () => {
    window.open(props.link);
  };

  if (isBlank(props.link)) {
    return null;
  }

  return (
    <div className={styles.linkToButtonWrap}>
      <div className={styles.linkToButton} onClick={handleClick}>
        View the generated app <img alt="" src={`${settings.S3CDN}${settings.viewAssetsPath}linkTo.svg`} />
      </div>
    </div>
  );
}
